import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import { errosPorCampo } from '../servicos/erros';

/**
 * Leva os erros por campo da API para os campos do react-hook-form.
 *
 * A API responde `{ message, erros: [{ campo, mensagem }] }`
 * (`apps/api/src/common/zod-validation.pipe.ts`) e os formulários usam os nomes
 * de campo do backend, então o casamento é direto — sem tabela de tradução para
 * alguém esquecer de atualizar.
 *
 * É a metade que o legado não fazia: lá a falha virava uma mensagem fixa no topo
 * ("Assessor já cadastrado!", independente da causa real, dívida técnica #50),
 * e o operador não sabia **qual** campo corrigir. Aqui o problema do servidor
 * cai embaixo do input que o causou, do mesmo jeito que o da validação local.
 *
 * O que a API relatar sobre campo que este formulário não tem continua visível:
 * `ErroDaApi` lista o que sobrou, para nenhum problema sumir em silêncio.
 */
export function aplicarErrosDaApi<T extends FieldValues>(
  erro: unknown,
  campos: readonly Path<T>[],
  definirErro: UseFormSetError<T>,
): void {
  const porCampo = errosPorCampo(erro);
  let primeiro = true;

  for (const campo of campos) {
    const mensagem = porCampo[campo];
    if (mensagem === undefined) continue;

    // O foco vai para o primeiro campo com problema: quem navega por teclado
    // não deveria caçar o erro percorrendo o formulário inteiro de novo.
    definirErro(campo, { type: 'server', message: mensagem }, { shouldFocus: primeiro });
    primeiro = false;
  }
}
